import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import type { StepConfig, StepProps } from '../Modal/MultiStepModal';

interface MultiStepWizardPageProps {
  title: string;
  subtitle?: string;
  breadcrumb?: React.ReactNode;
  steps: StepConfig[] | ((data: any) => StepConfig[]);
  onComplete: (data: any) => void | Promise<void>;
  onCancel: () => void;
  initialData?: any;
  completeLabel?: string;
  nextLabel?: string;
}

export default function MultiStepWizardPage({
  title,
  subtitle,
  breadcrumb,
  steps,
  onComplete,
  onCancel,
  initialData = {},
  completeLabel = 'Save template',
  nextLabel = 'Continue',
}: MultiStepWizardPageProps) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  const computedSteps = useMemo(() => {
    return typeof steps === 'function' ? steps(formData) : steps;
  }, [steps, formData]);

  useEffect(() => {
    if (currentStepIndex > computedSteps.length - 1) {
      setCurrentStepIndex(Math.max(0, computedSteps.length - 1));
    }
  }, [computedSteps.length, currentStepIndex]);

  const currentStep = computedSteps[currentStepIndex];
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === computedSteps.length - 1;

  const handleDataChange = useCallback((newData: any) => {
    setFormData((prev: any) => ({ ...prev, ...newData }));
  }, []);

  const handleNext = () => {
    if (isSubmitting) return;

    if (currentStep.validation && !currentStep.validation(formData)) {
      showToast(`Please complete all required fields in ${currentStep.title}`, 'error');
      return;
    }

    if (isLastStep) {
      void handleComplete();
      return;
    }

    setCurrentStepIndex((prev) => Math.min(prev + 1, computedSteps.length - 1));
  };

  const handleBack = () => {
    if (isSubmitting) return;
    if (!isFirstStep) setCurrentStepIndex((prev) => prev - 1);
  };

  const handleComplete = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      await Promise.resolve(onComplete(formData));
    } catch (e: any) {
      showToast(e?.response?.data?.message || e?.message || 'Failed to complete', 'error');
      return;
    } finally {
      setIsSubmitting(false);
    }
  };

  const StepComponent = currentStep?.component as React.ComponentType<StepProps> | undefined;
  const mergedStepData =
    currentStep?.stepDataPatch && typeof currentStep.stepDataPatch === 'object'
      ? { ...formData, ...(currentStep.stepDataPatch as Record<string, unknown>) }
      : formData;

  return (
    <div className="wizard-page-root py-6">
      <div className="mb-4 border-b border-[#E6E6E4] pb-3">
        <div className="flex items-center gap-2 text-xs text-[#515959] mb-4">
          <button
            type="button"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-[#D4D4D4] bg-white text-[#4B5563] hover:bg-[#F3F4F6]"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            <ChevronLeftIcon className="h-3 w-3" />
          </button>
          <span className="ml-2 text-[11px] text-[#6B7280]">
            {breadcrumb || (
              <>
                Settings <span className="mx-1 text-[#D1D5DB]">/</span> Certificate templates
              </>
            )}
          </span>
        </div>

        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-[#191919] leading-tight">{title}</h1>
            {subtitle ? (
              <p className="mt-1 text-xs font-normal text-[#515959]">{subtitle}</p>
            ) : null}
          </div>

          <div className="hidden md:flex md:flex-wrap md:items-center md:gap-3 md:justify-end">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center rounded-lg border border-transparent px-3 py-2 text-xs font-medium text-gray-500 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>

            {!isFirstStep ? (
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-white px-3 py-2 text-xs font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
                disabled={isSubmitting}
              >
                Back
              </button>
            ) : null}

            <button
              type="button"
              onClick={handleNext}
              className="inline-flex items-center justify-center rounded-lg bg-orange-500 px-4 py-2 text-xs font-medium text-white shadow-sm hover:bg-orange-600 transition-colors disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isLastStep
                ? isSubmitting
                  ? 'Saving…'
                  : completeLabel
                : nextLabel}
            </button>
          </div>
        </div>
      </div>

      <div className="wizard-page-card rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col min-h-0 max-md:max-h-[min(640px,calc(100dvh-11rem))]">
        <div className="modal-content-wrapper wizard-modal-inner flex-1 min-h-0" style={{ padding: 0 }}>
          <div className="step-indicator-sidebar md:border-r border-[#E5E7EB]">
            {computedSteps.map((step, index) => (
              <div
                key={step.id}
                className={`step-item ${
                  index === currentStepIndex ? 'active' : index < currentStepIndex ? 'completed' : 'inactive'
                }`}
              >
                <div className="step-dot"></div>
                <div className="step-content">
                  <div className="step-title">{step.title}</div>
                </div>
                {index < computedSteps.length - 1 && <div className="step-connector" />}
              </div>
            ))}
          </div>

          <div className="step-content-area flex flex-col flex-1 min-h-0 md:pl-4 pl-3 pr-3 pb-4 pt-3 md:pt-4">
            <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1">
              {StepComponent ? (
                <StepComponent
                  data={mergedStepData}
                  onDataChange={handleDataChange}
                  onNext={handleNext}
                  onBack={handleBack}
                  onComplete={handleComplete}
                  isFirstStep={isFirstStep}
                  isLastStep={isLastStep}
                  validateAndNext={handleNext}
                />
              ) : null}
            </div>

            <div className="md:hidden shrink-0 sticky bottom-0 -mx-3 px-3 pt-3 pb-1 mt-4 border-t border-gray-100 bg-white flex gap-2">
              {!isFirstStep ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex-1 inline-flex items-center justify-center rounded-lg border border-[#CDCDCD] bg-white px-3 py-2.5 text-xs font-medium text-[#383838] hover:bg-[#F3F2F0] transition-colors"
                  disabled={isSubmitting}
                >
                  Back
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onCancel}
                  className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={handleNext}
                className="flex-[1.35] inline-flex items-center justify-center rounded-lg bg-orange-500 px-3 py-2.5 text-xs font-medium text-white shadow-sm hover:bg-orange-600 transition-colors disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isLastStep
                  ? isSubmitting
                    ? 'Saving…'
                    : completeLabel
                  : nextLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
